import Building02Icon from "@redotech/redo-icons/building-02.svg";
import type { Store } from "@redotech/redo-partners-portal-rpc/schema/shared/store";
import { createStore } from "@redotech/redo-partners-portal-rpc/schema/stores/create-store";
import { toast } from "@redotech/redo-web/alert";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { CountrySelectDropdownInput } from "@redotech/redo-web/country-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { ClientError, clientErrorMessage } from "@redotech/rpc/errors";
import { memo, type ReactNode, useState } from "react";

import { useCreateStore } from "./use-stores";

interface StoreForm {
  name: string;
  storeUrl: string;
  email: string;
  phone: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

type TextField = Exclude<keyof StoreForm, "country">;

const EMPTY_FORM: StoreForm = {
  name: "",
  storeUrl: "",
  email: "",
  phone: "",
  street1: "",
  street2: "",
  city: "",
  state: "",
  zip: "",
  country: "US",
};

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

function countryName(code: string): string {
  try {
    return regionNames.of(code) ?? "";
  } catch {
    return "";
  }
}

function FormSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Flex dir="column" gap="md">
      <Text fontSize="sm" fontWeight="semibold" m="none">
        {title}
      </Text>
      {children}
    </Flex>
  );
}

export const CreateStoreModal = memo(function CreateStoreModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (store: Store) => void;
}) {
  const create = useCreateStore();
  const [form, setForm] = useState<StoreForm>(EMPTY_FORM);
  const [storeUrlError, setStoreUrlError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  // The server's schema, so a missing field fails before the request does.
  const input = createStore.input.safeParse({
    name: form.name,
    email: form.email,
    phone: form.phone,
    storeUrl: form.storeUrl,
    address: {
      name: form.name,
      street1: form.street1,
      street2: form.street2,
      city: form.city,
      state: form.state,
      zip: form.zip,
      country: form.country,
      countryName: countryName(form.country),
    },
  });

  const field = (key: TextField, label: string, placeholder: string) => (
    <RedoTextInput
      errorText={key === "storeUrl" ? storeUrlError : undefined}
      fullWidth
      htmlId={`create-store-${key}`}
      label={label}
      placeholder={placeholder}
      setValue={(value) => {
        setForm((current) => ({ ...current, [key]: value }));
        if (key === "storeUrl") {
          setStoreUrlError(undefined);
        }
      }}
      value={form[key]}
    />
  );

  const submit = async () => {
    if (!input.success) {
      return;
    }
    setStoreUrlError(undefined);
    setSubmitting(true);
    try {
      onCreated(await create(input.data));
    } catch (err) {
      const status = err instanceof ClientError ? err.statusCode : undefined;
      if (status === 409) {
        setStoreUrlError("Another Redo store already uses this URL.");
      } else if (status === 400) {
        // The form already matches the RPC schema, so a 400 is the server
        // rejecting the URL: unparseable, or a myshopify.com domain.
        setStoreUrlError(
          clientErrorMessage(err, "Enter a valid store URL, like acme.com."),
        );
      } else {
        toast(
          clientErrorMessage(err, "Couldn't create the store. Try again."),
          { variant: "error" },
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RedoModal
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Create store",
        disabled: !input.success,
        onClick: submit,
        pending: submitting,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      subtitle="Set up a new Redo store that your integration manages."
      title="Create store"
      TitleIcon={Building02Icon}
    >
      <Flex dir="column" gap="2xl">
        <FormSection title="Store">
          {field("name", "Store name", "Acme Outfitters")}
          {field("storeUrl", "Store URL", "acme-outfitters.com")}
        </FormSection>
        <FormSection title="Contact">
          <Flex gap="md">
            {field("email", "Email", "ops@acme.com")}
            {field("phone", "Phone", "+1 555 010 0100")}
          </Flex>
        </FormSection>
        <FormSection title="Address">
          {field("street1", "Street address", "1 Main St")}
          {field("street2", "Address line 2 (optional)", "Suite 100")}
          <Flex gap="md">
            {field("city", "City", "Provo")}
            {field("state", "State or region", "UT")}
          </Flex>
          <Flex gap="md">
            {field("zip", "Postal code", "84601")}
            <CountrySelectDropdownInput
              countryCode={form.country}
              fitToAnchor
              fullWidth
              label="Country"
              setCountryCode={(country) =>
                setForm((current) => ({ ...current, country }))
              }
            />
          </Flex>
        </FormSection>
      </Flex>
    </RedoModal>
  );
});
